import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  type LucideIcon,
  Settings2Icon,
  ShieldAlertIcon,
  UsersIcon,
  WarehouseIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { Logo } from "@/components/app/logo";
import { UserMenu } from "@/components/app/user-menu";
import { hasAccounts, signInUrl, useMe } from "@/lib/account";
import { matchPath, PageLink, useLocation } from "@/lib/pages";
import { createQueryClient } from "@/lib/query";
import { useTheme } from "@/lib/theme";
import { AdminTRPCProvider, adminClient, createAppClient, TRPCProvider } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { AuthCard } from "../account/account-app";
import { GaragePage, GaragesPage } from "./garages";
import { AdminSettingsPage } from "./settings";
import { UsersPage } from "./users";

/** /admin/…: the admin interface for platform admins (ADR 0011). */
export function AdminApp() {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!hasAccounts()) window.location.replace("/");
    else if (!isPending && !me) window.location.replace(signInUrl());
    else if (me && me.status !== "active") window.location.replace("/account/restricted");
  }, [me, isPending]);
  if (!hasAccounts() || !me || me.status !== "active") return null;
  if (me.platformRole !== "admin") {
    return (
      <AuthCard title={t("admin.notAllowedTitle")} body={t("admin.notAllowedBody")}>
        <a href="/" className="text-sm text-brand hover:underline">
          {t("admin.backToGarages")}
        </a>
      </AuthCard>
    );
  }
  return (
    <AdminTRPCProvider trpcClient={adminClient} queryClient={queryClient}>
      <AdminShell />
    </AdminTRPCProvider>
  );
}

const NAV: { href: string; key: "garages" | "users" | "settings"; icon: LucideIcon }[] = [
  { href: "/admin", key: "garages", icon: WarehouseIcon },
  { href: "/admin/users", key: "users", icon: UsersIcon },
  { href: "/admin/settings", key: "settings", icon: Settings2Icon },
];

function AdminShell() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const path = pathname.replace(/^\/admin/, "") || "/";
  const garage = matchPath("/garages/:name", path);
  const active = (key: string) =>
    key === "garages" ? path === "/" || path.startsWith("/garages") : path.startsWith(`/${key}`);

  let page: ReactNode;
  if (garage) page = <GaragePage name={garage.name ?? ""} />;
  else if (path === "/users") page = <UsersPage />;
  else if (path === "/settings") page = <AdminSettingsPage />;
  else page = <GaragesPage />;

  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-border bg-sidebar md:flex lg:w-64">
        <div className="flex h-16 items-center gap-2 px-4">
          <Logo />
        </div>
        <p className="flex items-center gap-1.5 px-5 pb-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          <ShieldAlertIcon className="size-3.5" />
          {t("admin.title")}
        </p>
        <nav className="flex flex-1 flex-col gap-0.5 px-3 py-2">
          {NAV.map((item) => (
            <PageLink
              key={item.key}
              href={item.href}
              aria-current={active(item.key) ? "page" : undefined}
              className={cn(
                "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                active(item.key)
                  ? "bg-surface text-foreground shadow-xs ring-1 ring-border"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <item.icon className={cn("size-[18px]", active(item.key) ? "text-brand" : "")} />
              {t(`admin.${item.key}`)}
            </PageLink>
          ))}
        </nav>
        <div className="flex flex-col gap-1 border-t border-border p-3">
          <a
            href="/"
            className="flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeftIcon className="size-[18px]" />
            {t("admin.backToGarages")}
          </a>
          <UserMenu />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-background/85 pt-[env(safe-area-inset-top)] backdrop-blur-md md:hidden">
          <div className="flex h-14 items-center justify-between px-4">
            <a href="/" aria-label={t("admin.backToGarages")}>
              <Logo />
            </a>
            <UserMenu variant="icon" />
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-2">
            {NAV.map((item) => (
              <PageLink
                key={item.key}
                href={item.href}
                aria-current={active(item.key) ? "page" : undefined}
                className={cn(
                  "flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium",
                  active(item.key)
                    ? "bg-surface text-foreground shadow-xs ring-1 ring-border"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <item.icon className="size-4" />
                {t(`admin.${item.key}`)}
              </PageLink>
            ))}
          </nav>
        </header>
        <main className="min-w-0 flex-1">{page}</main>
      </div>
      <Toaster
        theme={theme}
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast: "!rounded-xl !border-border !bg-surface-raised !text-foreground !shadow-lg",
          },
        }}
      />
    </div>
  );
}

/**
 * The garage API of one garage, for the admin's garage page: its own client and query cache, so
 * one garage's members never show on another's page.
 */
export function GarageScope({ name, children }: { name: string; children: ReactNode }) {
  const client = useMemo(() => createAppClient(name), [name]);
  const queryClient = useMemo(() => createQueryClient(), []);
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={client} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
